import { useEffect, useState } from 'react';
import { Button, Flex, LoadingOverlay, TextInput } from '@mantine/core';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import {
	FormField,
	StyledDrawer,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/styles.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	isValidTagName,
	TAG_NAME_MAX_LENGTH,
	TAG_NAME_VALIDATION_MESSAGE,
} from '@dealstack/shared/lib/common/business/tagName';

interface Props {
	opened: boolean;
	onClose: () => void;
	onCreate: (name: string) => void;
	isLoading?: boolean;
}

export const CreateTagDrawer = ({ opened, onClose, onCreate, isLoading = false }: Props) => {
	const tMipManage = useTranslationMipManage();
	const [name, setName] = useState('');

	useEffect(() => {
		if (opened) {
			setName('');
		}
	}, [opened]);

	const trimmed = name.trim();
	const isFormValid = isValidTagName(trimmed);
	const showError = trimmed.length > 0 && !isFormValid;

	const handleSave = () => {
		if (!isFormValid) {
			return;
		}
		onCreate(trimmed);
	};

	return (
		<StyledDrawer
			opened={opened}
			onClose={onClose}
			position="right"
			size="md"
			transitionProps={drawerTransitionProps}
			title={tMipManage('root.classifiers.tags.create_drawer.title')}
		>
			<Flex direction="column" h="100%" mih={400}>
				<LoadingOverlay visible={isLoading} overlayProps={{ blur: 2 }} />

				<div style={{ flex: 1 }}>
					<FormField>
						<TextInput
							label={tMipManage('root.classifiers.tags.create_drawer.label.name')}
							placeholder={tMipManage(
								'root.classifiers.tags.create_drawer.placeholder.name'
							)}
							size="md"
							required
							value={name}
							maxLength={TAG_NAME_MAX_LENGTH}
							error={
								showError
									? tMipManage(
											'root.classifiers.tags.create_drawer.error.invalid_name',
											{ defaultValue: TAG_NAME_VALIDATION_MESSAGE }
										)
									: undefined
							}
							onChange={(e) => setName(e.target.value)}
							disabled={isLoading}
						/>
					</FormField>
				</div>

				<Flex direction="column" align="flex-start" gap="1rem" mt="auto">
					<Button
						fullWidth
						onClick={handleSave}
						disabled={!isFormValid || isLoading}
						loading={isLoading}
					>
						{tMipManage('root.classifiers.tags.create_drawer.button.create')}
					</Button>
				</Flex>
			</Flex>
		</StyledDrawer>
	);
};
